/**
 * ArtifactWrapper - Container for Artifacts
 * TASK-286, TASK-289: Agentic Chat Artifact Rendering
 *
 * Provides consistent styling, header, and actions for all artifacts.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Download, Maximize2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Artifact } from '../../../types/chat.types';

interface ArtifactWrapperProps {
  artifact: Artifact;
  children: React.ReactNode;
}

const ArtifactWrapper: React.FC<ArtifactWrapperProps> = ({ artifact, children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Size classes
  const getSizeClasses = () => {
    switch (artifact.size) {
      case 'sm':
        return 'max-w-xs';
      case 'md':
        return 'max-w-md';
      case 'lg':
        return 'max-w-lg';
      case 'full':
        return 'w-full';
      default:
        return 'max-w-md';
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className={`
          ${getSizeClasses()}
          bg-white dark:bg-gray-800
          border border-gray-200 dark:border-gray-700
          rounded-xl overflow-hidden
          shadow-sm
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
            {artifact.title}
          </h4>

          <div className="flex items-center gap-1">
            {/* Export button */}
            <button
              onClick={() => {
                // Export artifact data
                const dataStr = JSON.stringify(artifact.data, null, 2);
                const blob = new Blob([dataStr], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${artifact.title.replace(/\s+/g, '_')}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title="Export data"
            >
              <Download className="w-3.5 h-3.5 text-gray-500" />
            </button>

            {/* Expand button */}
            <button
              onClick={() => setIsExpanded(true)}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title="Expand"
            >
              <Maximize2 className="w-3.5 h-3.5 text-gray-500" />
            </button>

            {/* Collapse button */}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              title={isCollapsed ? 'Expand' : 'Collapse'}
            >
              {isCollapsed ? (
                <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
              ) : (
                <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <AnimatePresence>
          {!isCollapsed && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <div className="p-3">{children}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Expanded Modal */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-8"
            onClick={() => setIsExpanded(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl max-h-[80vh] overflow-auto w-full"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 flex items-center justify-between px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {artifact.title}
                </h3>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">{children}</div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ArtifactWrapper;
